interface RichTextControllerOptions {
  range: () => Range | null
  beforeChange?: () => void
  afterChange?: () => void
  fixed?: boolean
  preserveLinkFocus?: boolean
  restoreFocus?: () => void
  externalLinks?: boolean
  scale?: () => number
}

// Self-contained so the document editor can inject this same controller into its sandboxed iframe.
export function createRichTextController(document: Document, toolbar: HTMLElement, options: RichTextControllerOptions) {
  const linkForm = toolbar.querySelector<HTMLElement>('[data-role="link-form"]')!
  const linkInput = linkForm.querySelector<HTMLInputElement>("input")!
  let savedRange: Range | null = null
  const sizeInput = toolbar.querySelector<HTMLInputElement>('[data-role="font-size"]')
  let sizeRange: Range | null = null

  function selectionFontPx(range: Range) {
    let node = range.startContainer
    if (node.nodeType === 1 && node.childNodes[range.startOffset]) node = node.childNodes[range.startOffset]
    const element = node.nodeType === 1 ? (node as Element) : node.parentElement!
    return parseFloat(document.defaultView!.getComputedStyle(element).fontSize) || 16
  }

  function formatPoints(px: number) {
    const points = Math.round(px * 0.75 * 2) / 2
    return `${points % 1 === 0 ? points : points.toFixed(1)}pt`
  }

  function applyFontSize(px: number, range: Range) {
    restore(range)
    if (!options.range()) return
    options.beforeChange?.()
    const existing = Array.from(document.body.querySelectorAll('font[size="7"]'))
    document.execCommand("fontSize", false, "7")
    const spans: HTMLElement[] = []
    document.body.querySelectorAll('font[size="7"]').forEach((font) => {
      if (existing.includes(font)) return
      let span = document.createElement("span")
      span.style.fontSize = formatPoints(Math.min(160, Math.max(6, px)))
      while (font.firstChild) span.appendChild(font.firstChild)
      font.replaceWith(span)
      const parent = span.parentElement
      if (
        parent?.tagName === "SPAN" &&
        parent.childNodes.length === 1 &&
        parent.style.length === 1 &&
        parent.style.fontSize
      ) {
        parent.style.fontSize = span.style.fontSize
        while (span.firstChild) parent.appendChild(span.firstChild)
        span.remove()
        span = parent
      }
      spans.push(span)
    })
    if (spans.length) {
      const restored = document.createRange()
      restored.setStartBefore(spans[0])
      restored.setEndAfter(spans[spans.length - 1])
      restore(restored)
    }
    options.afterChange?.()
    update()
  }

  function focusSize() {
    const range = options.range()
    sizeRange = range?.cloneRange() ?? null
    sizeInput?.select()
  }

  function commitSize() {
    const range = sizeRange
    const points = parseFloat(sizeInput?.value ?? "")
    sizeRange = null
    sizeInput?.blur()
    if (range && Number.isFinite(points) && points > 0) applyFontSize(points / 0.75, range)
    else update()
  }

  function sizeKeydown(event: KeyboardEvent) {
    if (event.key !== "Enter" && event.key !== "Escape") return
    event.preventDefault()
    if (event.key === "Escape" && sizeInput) sizeInput.value = ""
    commitSize()
  }

  function finishSize() {
    if (sizeRange) commitSize()
  }

  function anchorFor(range: Range) {
    const node = range.commonAncestorContainer
    const element = node.nodeType === 1 ? (node as Element) : node.parentElement
    return element?.closest("a")
  }

  function hide() {
    toolbar.hidden = true
    linkForm.hidden = true
    savedRange = null
    sizeRange = null
  }

  function update() {
    if (sizeRange) return
    if (options.preserveLinkFocus && !linkForm.hidden) return
    const range = options.range()
    const rect = range?.getBoundingClientRect()
    if (!range || !rect || (!rect.width && !rect.height)) {
      hide()
      return
    }
    toolbar.hidden = false
    for (const command of ["bold", "italic", "underline"]) {
      toolbar
        .querySelector(`[data-action="${command}"]`)
        ?.setAttribute("aria-pressed", String(document.queryCommandState(command)))
    }
    toolbar.querySelector('[data-action="link"]')?.setAttribute("aria-pressed", String(Boolean(anchorFor(range))))
    if (sizeInput && document.activeElement !== sizeInput)
      sizeInput.value = formatPoints(selectionFontPx(range)).replace("pt", "")
    position(range)
  }

  function position(range: Range) {
    const rect = range.getBoundingClientRect()
    const view = document.defaultView!
    const x = options.fixed ? 0 : view.scrollX
    const y = options.fixed ? 0 : view.scrollY
    const width = options.fixed ? document.documentElement.clientWidth : document.documentElement.scrollWidth
    const scale = options.scale?.() ?? 1
    const toolbarWidth = toolbar.offsetWidth * scale
    toolbar.style.left = `${Math.max(4, Math.min(rect.left + rect.width / 2 + x - toolbarWidth / 2, width - toolbarWidth - 4))}px`
    const above = rect.top + y - toolbar.offsetHeight * scale - 8
    toolbar.style.top = `${above < y + 4 ? rect.bottom + y + 8 : above}px`
  }

  function restore(range: Range) {
    options.restoreFocus?.()
    const selection = document.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  function command(action: string, value?: string) {
    options.beforeChange?.()
    document.execCommand(action, false, value)
    options.afterChange?.()
    update()
  }

  function cancelLink() {
    linkForm.hidden = true
    if (savedRange) restore(savedRange)
    savedRange = null
    update()
  }

  function applyLink() {
    let url = linkInput.value.trim()
    if (!url || !savedRange) {
      cancelLink()
      return
    }
    if (!/^(https?|mailto|tel):/i.test(url) && !url.startsWith("#") && !url.startsWith("/")) url = `https://${url}`
    restore(savedRange)
    options.beforeChange?.()
    document.execCommand("createLink", false, url)
    if (options.externalLinks) {
      const range = options.range()
      const anchor = range && anchorFor(range)
      anchor?.setAttribute("target", "_blank")
      anchor?.setAttribute("rel", "noopener noreferrer")
    }
    options.afterChange?.()
    savedRange = null
    linkForm.hidden = true
    update()
  }

  function click(event: MouseEvent) {
    const target = event.target as Element
    const action = target.closest<HTMLButtonElement>("button")?.dataset.action
    if (!action) return
    event.preventDefault()
    if (action === "apply-link") applyLink()
    else if (action === "cancel-link") cancelLink()
    else if (action === "link") {
      const range = options.range()
      if (!range) return
      if (anchorFor(range)) command("unlink")
      else {
        savedRange = range.cloneRange()
        linkInput.value = ""
        linkForm.hidden = false
        if (options.preserveLinkFocus) position(range)
        linkInput.focus()
      }
    } else if (["bold", "italic", "underline"].includes(action)) {
      if (options.range()) command(action)
    } else if (sizeInput && (action === "smaller" || action === "larger")) {
      const range = options.range()
      if (range) applyFontSize(selectionFontPx(range) * (action === "larger" ? 1.125 : 1 / 1.125), range)
    }
  }

  function mousedown(event: MouseEvent) {
    if ((event.target as Element).tagName !== "INPUT") event.preventDefault()
  }

  function keydown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === "Escape") {
      event.preventDefault()
      if (event.key === "Enter") applyLink()
      else cancelLink()
    }
  }

  toolbar.addEventListener("mousedown", mousedown)
  toolbar.addEventListener("click", click)
  linkInput.addEventListener("keydown", keydown)
  sizeInput?.addEventListener("focus", focusSize)
  sizeInput?.addEventListener("keydown", sizeKeydown)
  sizeInput?.addEventListener("change", finishSize)
  sizeInput?.addEventListener("blur", finishSize)
  return {
    update,
    hide,
    get linkOpen() {
      return !linkForm.hidden
    },
    get fontSizeOpen() {
      return Boolean(sizeRange)
    },
    destroy() {
      toolbar.removeEventListener("mousedown", mousedown)
      toolbar.removeEventListener("click", click)
      linkInput.removeEventListener("keydown", keydown)
      sizeInput?.removeEventListener("focus", focusSize)
      sizeInput?.removeEventListener("keydown", sizeKeydown)
      sizeInput?.removeEventListener("change", finishSize)
      sizeInput?.removeEventListener("blur", finishSize)
    },
  }
}
